import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Flask, Plus, MagnifyingGlass, Barcode, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ChangeLotDialog, { GR10_LABEL } from './ChangeLotDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const SIDE = { left: 'GAUCHE', right: 'DROIT' };
const DEVICE_STATUS = { in_production: 'En production', control_pending: 'Contrôle final à réaliser', released: 'Libéré', blocked: 'Bloqué', cancelled: 'Annulé' };
const lotStatus = (l) => (l.status === 'closed' ? { label: 'Clôturé', cls: 'bg-zinc-400' } : (l.active_on_tanks || []).length ? { label: `Actif — ${l.active_on_tanks.join(', ')}`, cls: 'bg-emerald-600' } : { label: 'Disponible (non monté)', cls: 'bg-zinc-600' });

function TankCard({ tank, onChangeLot }) {
  const [showHistory, setShowHistory] = useState(false);
  const usages = [...(tank.lot_usages || [])].reverse();
  return (
    <div className={`border rounded-sm bg-white p-4 ${tank.active_lot_id ? 'border-zinc-200' : 'border-red-300'}`} data-testid={`tank-card-${tank.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold text-zinc-900">{tank.name}</h3>
          <p className="text-xs text-zinc-500">{tank.machine ? `Machine : ${tank.machine}` : 'Machine non renseignée'}{tank.default_print_profile ? ` · Profil : ${tank.default_print_profile}` : ''}</p>
        </div>
        <Button size="sm" onClick={() => onChangeLot(tank)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white text-xs" data-testid={`change-lot-btn-${tank.id}`}><Flask size={14} className="mr-1" />{tank.active_lot_id ? 'Changer le lot' : 'Renseigner le lot'}</Button>
      </div>
      <div className={`mt-3 p-3 rounded-sm ${tank.active_lot_id ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`} data-testid={`tank-active-lot-${tank.id}`}>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500">Lot actif</p>
        {tank.active_lot_id ? (
          <>
            <p className="font-mono font-semibold text-lg text-zinc-900">{tank.active_lot_number}</p>
            <p className="text-xs text-zinc-600">Depuis le {fmt(tank.active_lot_since)} · {tank.active_lot_set_by_name || '—'}{tank.active_lot?.expiry_date ? ` · Péremption ${tank.active_lot.expiry_date}` : ''}</p>
          </>
        ) : <p className="text-sm text-red-700 font-medium">Aucun lot actif — production GR-10 bloquée sur ce bac</p>}
      </div>
      {usages.length > 0 && (
        <div className="mt-3">
          <button type="button" onClick={() => setShowHistory(v => !v)} className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1" data-testid={`tank-history-toggle-${tank.id}`}><ClockCounterClockwise size={14} />Historique des lots ({usages.length})</button>
          {showHistory && (
            <ul className="mt-2 text-xs space-y-1 border-l border-zinc-200 pl-3" data-testid={`tank-history-${tank.id}`}>
              {usages.map(u => (
                <li key={u.id}><span className="font-mono font-semibold">{u.lot_number}</span> — ouvert {fmt(u.opened_at)} ({u.opened_by_name}){u.closed_at ? ` · clôturé ${fmt(u.closed_at)} (${u.closed_by_name})` : ' · en cours'}{u.previous_lot_number ? ` · remplace ${u.previous_lot_number}` : ''} · nettoyage confirmé</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function NewTankDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState(''); const [machine, setMachine] = useState(''); const [profile, setProfile] = useState('');
  useEffect(() => { if (open) { setName(''); setMachine(''); setProfile(''); } }, [open]);
  const submit = async () => {
    try {
      await axios.post(`${API}/resin-tanks`, { name, machine: machine || null, default_print_profile: profile || null });
      toast.success('Bac créé'); onCreated(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[92vw] sm:max-w-sm" data-testid="new-tank-dialog">
        <DialogHeader><DialogTitle>Nouveau bac / machine</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Nom du bac *</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="ex. Bac 2" className="rounded-sm mt-1" data-testid="new-tank-name" /></div>
          <div><Label className="text-xs">Machine</Label><Input value={machine} onChange={e => setMachine(e.target.value)} placeholder="ex. Asiga MAX" className="rounded-sm mt-1" data-testid="new-tank-machine" /></div>
          <div><Label className="text-xs">Profil d'impression par défaut</Label><Input value={profile} onChange={e => setProfile(e.target.value)} placeholder="ex. GR-10 50 µm" className="rounded-sm mt-1" data-testid="new-tank-profile" /></div>
        </div>
        <DialogFooter className="gap-2"><Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Annuler</Button><Button onClick={submit} disabled={!name.trim()} className="rounded-sm bg-zinc-900 text-white" data-testid="new-tank-submit">Créer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ResinLotsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tanks, setTanks] = useState([]);
  const [lots, setLots] = useState([]);
  const [lotQuery, setLotQuery] = useState(params.get('lot') || '');
  const [lotDevices, setLotDevices] = useState(null);
  const [changeTank, setChangeTank] = useState(null);
  const [newTankOpen, setNewTankOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, l] = await Promise.all([axios.get(`${API}/resin-tanks`), axios.get(`${API}/material-lots`, { params: { include_closed: true } })]);
      setTanks(t.data.items || []); setLots(l.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de chargement'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const searchLot = useCallback(async (q) => {
    const v = (q || '').trim();
    if (!v) { setLotDevices(null); return; }
    try {
      const r = await axios.get(`${API}/devices`, { params: { lot: v, limit: 500 } });
      setLotDevices({ lot: v, items: r.data.items || [] });
      setParams({ lot: v });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  }, [setParams]);
  useEffect(() => { const q = params.get('lot'); if (q) { setLotQuery(q); searchLot(q); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="resin-lots-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="rounded-sm -ml-2 mb-1 text-xs" data-testid="lots-back-btn"><ArrowLeft size={14} className="mr-1" />Tableau de bord</Button>
          <p className="label-overline">Traçabilité matière</p>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight flex items-center gap-3"><Flask size={32} />Lots de résine</h1>
          <p className="text-xs text-zinc-500 mt-1">{GR10_LABEL}</p>
        </div>
        <Button variant="outline" onClick={() => setNewTankOpen(true)} className="rounded-sm text-xs" data-testid="new-tank-btn"><Plus size={14} className="mr-1" />Nouveau bac / machine</Button>
      </div>

      <section data-testid="tanks-section">
        <h2 className="font-heading font-semibold text-sm text-zinc-800 mb-2">Bacs / machines — lot actif</h2>
        <div className="grid md:grid-cols-2 gap-4">{tanks.map(t => <TankCard key={t.id} tank={t} onChangeLot={setChangeTank} />)}</div>
      </section>

      <section className="border border-zinc-200 rounded-sm bg-white" data-testid="lot-search-section">
        <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-200 bg-zinc-50"><MagnifyingGlass size={16} /><h2 className="font-heading font-semibold text-sm text-zinc-800">Dispositifs associés à un lot</h2></div>
        <div className="p-4 space-y-3">
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); searchLot(lotQuery); }} data-testid="lot-search-form">
            <Input value={lotQuery} onChange={e => setLotQuery(e.target.value)} placeholder="Numéro de lot" className="rounded-sm font-mono max-w-xs" data-testid="lot-search-input" />
            <Button type="submit" className="rounded-sm bg-zinc-900 text-white" data-testid="lot-search-submit">Rechercher</Button>
          </form>
          {lotDevices && (
            <div data-testid="lot-devices-result">
              <p className="text-sm mb-2">Lot <span className="font-mono font-semibold">{lotDevices.lot}</span> — <span data-testid="lot-devices-count">{lotDevices.items.length}</span> dispositif(s)</p>
              {lotDevices.items.length > 0 && (
                <Table>
                  <TableHeader><TableRow><TableHead>ID HAMAC</TableHead><TableHead>N° série</TableHead><TableHead>Côté</TableHead><TableHead>Réf. patient</TableHead><TableHead>Démarrage</TableHead><TableHead>Opérateur</TableHead><TableHead>Bac / machine</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {lotDevices.items.map(d => (
                      <TableRow key={d.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => navigate(`/device/${encodeURIComponent(d.hamac_id || d.serial_number)}`)} data-testid={`lot-device-row-${d.hamac_id}`}>
                        <TableCell className="font-mono font-semibold"><span className="flex items-center gap-1"><Barcode size={14} />{d.hamac_id}</span></TableCell>
                        <TableCell className="font-mono text-xs">{d.serial_number}</TableCell>
                        <TableCell className="text-xs">{SIDE[d.side] || d.side}</TableCell>
                        <TableCell className="text-xs">{d.patient_reference || '—'}</TableCell>
                        <TableCell className="text-xs">{fmt(d.production_start?.started_at)}</TableCell>
                        <TableCell className="text-xs">{d.production_start?.operator_name || '—'}</TableCell>
                        <TableCell className="text-xs">{[d.fabrication?.tank_name, d.fabrication?.machine].filter(Boolean).join(' — ') || '—'}</TableCell>
                        <TableCell><Badge className="rounded-sm bg-zinc-800 text-white text-[10px]">{DEVICE_STATUS[d.status] || d.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="border border-zinc-200 rounded-sm bg-white" data-testid="lots-section">
        <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-200 bg-zinc-50"><Flask size={16} /><h2 className="font-heading font-semibold text-sm text-zinc-800">Référentiel des lots</h2><span className="ml-auto text-xs text-zinc-500">{lots.length}</span></div>
        <Table>
          <TableHeader><TableRow><TableHead>N° de lot</TableHead><TableHead>Fournisseur</TableHead><TableHead>Péremption</TableHead><TableHead>Ouvert le</TableHead><TableHead>Actif sur</TableHead><TableHead>Dispositifs</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
          <TableBody>
            {lots.length === 0 && <TableRow><TableCell colSpan={7} className="text-sm italic text-zinc-400">Aucun lot enregistré — renseignez le lot actif d'un bac ci-dessus.</TableCell></TableRow>}
            {lots.map(l => (
              <TableRow key={l.id} className="cursor-pointer hover:bg-zinc-50" onClick={() => { setLotQuery(l.lot_number); searchLot(l.lot_number); }} data-testid={`lot-row-${l.lot_number}`}>
                <TableCell className="font-mono font-semibold">{l.lot_number}</TableCell>
                <TableCell className="text-xs">{l.supplier || '—'}</TableCell>
                <TableCell className="text-xs">{l.expiry_date || '—'}</TableCell>
                <TableCell className="text-xs">{fmt(l.opened_at)}</TableCell>
                <TableCell className="text-xs">{(l.active_on_tanks || []).length ? l.active_on_tanks.join(', ') : '—'}</TableCell>
                <TableCell className="text-xs font-semibold" data-testid={`lot-count-${l.lot_number}`}>{l.device_count}</TableCell>
                <TableCell><Badge className={`rounded-sm text-[10px] text-white ${lotStatus(l).cls}`}>{lotStatus(l).label}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      <ChangeLotDialog tank={changeTank} open={!!changeTank} onOpenChange={(v) => { if (!v) setChangeTank(null); }} onChanged={load} />
      <NewTankDialog open={newTankOpen} onOpenChange={setNewTankOpen} onCreated={load} />
    </div>
  );
}
